import React, {useCallback, useState} from 'react';
import {useDispatch} from 'react-redux';

import debounce from 'lodash.debounce';
import {searchPresets} from 'src/openapi/admin/apis/EntitlementsPresetSearchApi';
import {useAppDispatch} from 'src/store/hooks';

import {Autosuggest} from '@pb/kit-component-autosuggest-v2';
import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';

import {presetsStateActions} from '../slices/presets-state.slice';

import {PresetSuggestResult} from './preset-suggest-result';

import type {ChangeEventHandler} from 'react';
import type {PresetItem} from 'src/openapi/admin/models/PresetItem';
import type {ServerResponse} from 'src/typedef';

type Props = {
    id: string,
};

const MAX_RESULTS = 30;

export const PresetSuggest = (props: Props) => {
    const {id} = props;
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const {createAbortSignal, abort} = useAbortSignal();

    const [query, setQuery] = useState('');
    const [busy, setBusy] = useState(false);
    const [response, setResponse] = useState<ServerResponse<PresetItem[]> | null>(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const getSuggestions = useCallback(debounce(async (newValue: string) => {
        const signal = createAbortSignal();
        const presetNamePrefix = newValue.toLowerCase().trim();
        if (presetNamePrefix && presetNamePrefix.length > 1) {
            const searchResult = await apiDispatch(searchPresets({searchPresetRequest: {
                presetNamePrefix,
                searchResultSize: MAX_RESULTS,
            }}, {signal}));
            if (searchResult.payload.success) {
                setResponse({data: searchResult.payload.data});
            } else {
                setResponse({error: {
                    message: 'Can\'t suggest presets. Service is temporary unavailable.',
                }});
            }
            setBusy(false);
        } else {
            setBusy(false);
            setResponse(null);
            abort();
        }
    }, 1000), []);

    const onChangeQuery = useCallback<ChangeEventHandler<HTMLInputElement>>(event => {
        const value = event.currentTarget.value;
        setBusy(true);
        setQuery(value);
        getSuggestions(value);
    }, [getSuggestions]);

    const clearSuggest = useCallback(() => {
        setQuery('');
        dispatch(presetsStateActions.setFilters({}));
        setResponse(null);
        abort();
    }, [dispatch, abort]);

    const onItemSelect = useCallback((preset: PresetItem) => {
        dispatch(presetsStateActions.setFilters({preset}));
        setQuery(preset.name);
        setResponse(null);
    }, [dispatch]);

    return (
        <div style={{width: 500}}>
            <Autosuggest
                id={id}
                value={query}
                maxLength={64}
                onChange={onChangeQuery}
                placeholder="Search for preset by keyword"
                onItemSelect={onItemSelect}
                onClear={clearSuggest}>
                <PresetSuggestResult
                    id={id}
                    query={query.trim()}
                    busy={busy}
                    response={response}
                />
            </Autosuggest>
        </div>
    );
};
